import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { IMAGE } from "../../utilities/constants/MediaType";
import { EVENT_PAGE_URL } from "../../utilities/constants/PagesEndpoints";
import { getMediaByEventId } from "./EventServices";

/**
 * A card that displays an event preview.
 * @param {object} event The event to be displayed.
 * @returns A preview event card element.
 */
const EventPreviewCard = ({ event }) => {
  const navigateTo = useNavigate();
  const [mediaData, setMediaData] = useState([]);

  useEffect(() => {
    getMediaByEventId(setMediaData, event.id);
  }, [event.id]);

  // Format date nicely
  const eventDate = event.postedDate
    ? new Date(event.postedDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  // Get the first image for the preview or a fallback
  const getMediaUrl = (mediaItem) =>
    mediaItem?.sharedMedia?.mediaUrl || mediaItem?.mediaUrl;
  const mediaList = Array.isArray(mediaData) ? mediaData : [];
  const firstMedia = mediaList.find((m) => {
    const type = m?.sharedMedia?.type || m?.type;
    return type && type.startsWith(IMAGE);
  });

  // If no image found, check for ANY media with a URL, or fallback
  const previewImage = firstMedia
    ? getMediaUrl(firstMedia)
    : mediaList.length > 0
      ? getMediaUrl(mediaList[0])
      : "assets/images/image_placeholder_icon.png";

  return (
    <div
      className="group h-full w-full cursor-pointer focus:outline-none perspective-1000"
      onClick={() => navigateTo(`${EVENT_PAGE_URL}/${event.id}`)}
    >
      <div className="relative flex flex-col h-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform group-hover:-translate-y-2 overflow-hidden border border-slate-100 dark:border-slate-700">
        {/* Image Container with Overlay */}
        <div className="relative w-full h-64 overflow-hidden">
          <img
            src={previewImage}
            alt={event.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>

          {/* Badge / Category Tag could go here */}
          <div className="absolute top-4 right-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-slate-800 dark:text-white shadow-sm">
            Event
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-grow p-6">
          {/* Date */}
          {eventDate && (
            <div className="text-xs font-semibold tracking-wider text-blue-600 dark:text-blue-400 uppercase mb-2">
              {eventDate}
            </div>
          )}

          {/* Title */}
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {event.title}
          </h3>

          {/* Description */}
          <p className="text-slate-600 dark:text-slate-300 text-sm line-clamp-2 leading-relaxed mb-4 flex-grow">
            {event.description}
          </p>

          {/* Action Footer */}
          <div className="flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 mt-auto group/btn">
            <span>View Details</span>
            <svg
              className="w-4 h-4 ml-2 transform transition-transform duration-300 group-hover/btn:translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPreviewCard;
